import React, { createContext, useContext, useMemo } from 'react';
import { User } from '@supabase/supabase-js';
import { useAuth } from '@/hooks/use-auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithAzure: () => Promise<void>;
  signOut: () => Promise<void>;
  uploadAvatar: (file: File) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = React.memo(({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => auth, [
    auth.user?.id, // Only re-render if user ID changes
    auth.loading,
    auth.signInWithAzure,
    auth.signOut,
    auth.uploadAvatar
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
});

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
} 